import {
  getTaskPublic as GET_TASK_PUBLIC,
  updateTaskPublic as UPDATE_TASK_PUBLIC,
  UpdateTaskPaymentPublicStatus,
  TaskGuest,
} from '@admiin-com/ds-graphql';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useSnackbar } from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useClientContext } from '../../components/ApolloClientProvider/ApolloClientProvider';
import { useIsLoggedIn } from '../useIsLoggedIn/useIsLoggedIn';

export const useGuestTask = (
  token?: string | null,
  skipClientTypeCheck?: boolean
) => {
  const { t } = useTranslation('taskbox');
  const showSnackbar = useSnackbar();
  const { clientType, setClientType } = useClientContext();
  const { isLoggedIn } = useIsLoggedIn();

  const invalidApolloClient = !isLoggedIn && clientType === 'userPool';

  useEffect(() => {
    if (skipClientTypeCheck) return;

    const figureAuth = async () => {
      let user;
      try {
        user = await fetchAuthSession({ forceRefresh: true });
      } catch (err) {
        console.log('ERROR refresh user session: ', err);
      }

      if ((!user?.tokens && clientType === 'userPool') || !clientType) {
        setClientType('iam');
      } else if (user?.tokens && clientType === 'iam') {
        setClientType('userPool');
      }
    };

    figureAuth();
  }, [isLoggedIn, clientType, setClientType, skipClientTypeCheck]);

  const { data, loading, error, refetch } = useQuery(gql(GET_TASK_PUBLIC), {
    variables: { token },
    skip: !token || invalidApolloClient,
    onError: (err) => {
      if (err?.message !== 'TOKEN_EXPIRED') {
        showSnackbar({
          message: t(err.message, { ns: 'errors' }),
          severity: 'error',
          horizontal: 'right',
          vertical: 'bottom',
        });
      }
    },
  });

  const taskGuest: TaskGuest | undefined = data?.getTaskPublic;

  const [updateTaskPublic, { loading: updating }] = useMutation(
    gql(UPDATE_TASK_PUBLIC)
  );

  const REGENERATE_TASK_PUBLIC_LINK = /* GraphQL */ `
    mutation RegenerateTaskPublicLink($token: ID!) {
      regenerateTaskPublicLink(token: $token)
    }
  `;

  const [regenerateTaskPublicLink, { loading: regenerating }] = useMutation(
    gql(REGENERATE_TASK_PUBLIC_LINK)
  );

  const markTaskAsPaid = useCallback(async () => {
    if (!token) return;
    try {
      const result = await updateTaskPublic({
        variables: {
          input: {
            token,
            paymentStatus: UpdateTaskPaymentPublicStatus.MARKED_AS_PAID,
          },
        },
      });

      // Check for GraphQL errors even when data is returned
      if (result.errors && result.errors.length > 0) {
        const errorMessage = result.errors[0].message;
        throw new Error(errorMessage);
      }

      // Check if the mutation actually returned data
      if (!result.data?.updateTaskPublic) {
        throw new Error('MARK_AS_PAID_FAILED');
      }

      showSnackbar({
        message: t('markAsPaidSuccess', { ns: 'taskbox' }),
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });
      await refetch();
    } catch (err: any) {
      showSnackbar({
        message: t(err.message, { ns: 'errors' }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
      throw err; // Re-throw to allow components to handle the error state
    }
  }, [token, updateTaskPublic, showSnackbar, t, refetch]);

  const regenerateLink = useCallback(async () => {
    if (!token) return;
    try {
      await regenerateTaskPublicLink({ variables: { token } });
      showSnackbar({
        message: t('newLinkSent', { ns: 'taskbox' }),
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });
    } catch (err: any) {
      showSnackbar({
        message: t(err.message, { ns: 'errors' }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    }
  }, [token, regenerateTaskPublicLink, showSnackbar, t]);

  const tokenExpired = error?.message === 'TOKEN_EXPIRED';

  return {
    taskGuest,
    loading,
    updating,
    refetch,
    markTaskAsPaid,
    tokenExpired,
    regenerateLink,
    regenerating,
  };
};
